import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, query, where, orderBy,
  getDocs, updateDoc, doc, serverTimestamp, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { captureLocation, formatCoordinates, openInGoogleMaps } from '../utils/locationTracker';
import './JourneyPlan.css';

function JourneyPlan({ user, onBack }) {
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [todayPlanId, setTodayPlanId] = useState(null);

  const [form, setForm] = useState({
    outletName: '',
    address: '',
    contactPerson: '',
    contactPhone: '',
    purpose: 'routine_service',
    notes: '',
    visitOrder: ''
  });

  const purposeOptions = [
    { value: 'routine_service', label: '🔧 Routine Service', color: '#6366f1' },
    { value: 'emergency',       label: '🚨 Emergency',        color: '#ef4444' },
    { value: 'installation',    label: '⚙️ Installation',     color: '#f59e0b' },
    { value: 'inspection',      label: '🔍 Inspection',       color: '#10b981' },
    { value: 'delivery',        label: '📦 Delivery',         color: '#3b82f6' },
  ];

  useEffect(() => {
    loadTodayJourney();
  }, []);

  const getTodayRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return { today, tomorrow };
  };

  const loadTodayJourney = async () => {
    setLoading(true);
    try {
      const { today, tomorrow } = getTodayRange();

      const q = query(
        collection(db, 'journeyPlans'),
        where('userId', '==', user.id),
        where('date', '>=', today.getTime()),
        where('date', '<', tomorrow.getTime()),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const plans = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Sort by visitOrder then by creation time
      plans.sort((a, b) => (a.visitOrder || 999) - (b.visitOrder || 999));
      setStops(plans);

    } catch (error) {
      console.error('❌ Error loading journey:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStop = async () => {
    if (!form.outletName.trim()) {
      alert('Please enter an outlet name.');
      return;
    }
    setActionLoading(true);
    try {
      const location = await captureLocation(user, 'JourneyPlan', { action: 'add_stop' });

      const stopData = {
        userId: user.id,
        userName: user.name,
        userRole: user.role,
        outletName: form.outletName.trim(),
        address: form.address.trim(),
        contactPerson: form.contactPerson.trim(),
        contactPhone: form.contactPhone.trim(),
        purpose: form.purpose,
        notes: form.notes.trim(),
        visitOrder: parseInt(form.visitOrder) || stops.length + 1,
        status: 'pending',   // pending | visited | skipped
        date: Date.now(),
        serverTimestamp: serverTimestamp(),
        planLocation: location ? {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        } : null,
        visitedAt: null,
        visitLocation: null,
        visitNotes: ''
      };

      await addDoc(collection(db, 'journeyPlans'), stopData);
      setForm({ outletName: '', address: '', contactPerson: '', contactPhone: '', purpose: 'routine_service', notes: '', visitOrder: '' });
      setShowAddForm(false);
      await loadTodayJourney();
      showToast('✅ Stop added to journey!');

    } catch (error) {
      console.error('❌ Add stop error:', error);
      alert('❌ Failed to add stop. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkVisited = async (stop) => {
    setActionLoading(true);
    try {
      const location = await captureLocation(user, 'JourneyVisit', { action: 'mark_visited', outletName: stop.outletName });

      await updateDoc(doc(db, 'journeyPlans', stop.id), {
        status: 'visited',
        visitedAt: Date.now(),
        visitLocation: location ? {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        } : null
      });

      await loadTodayJourney();
      showToast(`✅ ${stop.outletName} marked as visited!`);

    } catch (error) {
      console.error('❌ Mark visited error:', error);
      alert('❌ Failed to update stop.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkSkipped = async (stop) => {
    if (!window.confirm(`Skip visit to ${stop.outletName}?`)) return;
    try {
      await updateDoc(doc(db, 'journeyPlans', stop.id), {
        status: 'skipped',
        skippedAt: Date.now()
      });
      await loadTodayJourney();
      showToast(`⏭️ ${stop.outletName} skipped.`);
    } catch (error) {
      console.error('❌ Skip error:', error);
    }
  };

  const handleDeleteStop = async (stop) => {
    if (!window.confirm(`Remove ${stop.outletName} from your journey?`)) return;
    try {
      await deleteDoc(doc(db, 'journeyPlans', stop.id));
      await loadTodayJourney();
      showToast('🗑️ Stop removed.');
    } catch (error) {
      console.error('❌ Delete stop error:', error);
    }
  };

  const handleUndoVisit = async (stop) => {
    try {
      await updateDoc(doc(db, 'journeyPlans', stop.id), {
        status: 'pending',
        visitedAt: null,
        visitLocation: null
      });
      await loadTodayJourney();
      showToast('↩️ Marked as pending again.');
    } catch (error) {
      console.error('❌ Undo error:', error);
    }
  };

  const getPurposeInfo = (value) => purposeOptions.find(p => p.value === value) || purposeOptions[0];

  const getStats = () => {
    const total   = stops.length;
    const visited = stops.filter(s => s.status === 'visited').length;
    const skipped = stops.filter(s => s.status === 'skipped').length;
    const pending = stops.filter(s => s.status === 'pending').length;
    const progress = total > 0 ? Math.round((visited / total) * 100) : 0;
    return { total, visited, skipped, pending, progress };
  };

  const showToast = (message) => {
    document.querySelectorAll('.toast-notification').forEach(t => t.remove());
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.innerHTML = `<div class="toast-icon">🗺️</div><div class="toast-message">${message}</div>`;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  const stats = getStats();

  // ── LOADING ───────────────────────────────────────────────
  if (loading) {
    return (
      <div className="journey-page">
        <div className="journey-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h1>🗺️ Journey Plan</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading today's journey...</p>
        </div>
      </div>
    );
  }

  // ── MAIN UI ───────────────────────────────────────────────
  return (
    <div className="journey-page">

      {/* Header */}
      <div className="journey-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="journey-header-text">
          <h1>🗺️ Journey Plan</h1>
          <p className="journey-date">{new Date().toLocaleDateString('en-KE', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
        <button className="add-stop-btn" onClick={() => setShowAddForm(true)}>+ Add Stop</button>
      </div>

      {/* Progress Bar */}
      {stats.total > 0 && (
        <div className="journey-progress-card">
          <div className="progress-top">
            <span className="progress-label">Today's Progress</span>
            <span className="progress-pct">{stats.progress}%</span>
          </div>
          <div className="progress-bar-track">
            <div className="progress-bar-fill" style={{ width: `${stats.progress}%` }}></div>
          </div>
          <div className="progress-stats">
            <div className="pstat">
              <span className="pstat-val visited-col">{stats.visited}</span>
              <span className="pstat-lbl">Visited</span>
            </div>
            <div className="pstat">
              <span className="pstat-val pending-col">{stats.pending}</span>
              <span className="pstat-lbl">Pending</span>
            </div>
            <div className="pstat">
              <span className="pstat-val skipped-col">{stats.skipped}</span>
              <span className="pstat-lbl">Skipped</span>
            </div>
            <div className="pstat">
              <span className="pstat-val total-col">{stats.total}</span>
              <span className="pstat-lbl">Total</span>
            </div>
          </div>
        </div>
      )}

      {/* Add Stop Form */}
      {showAddForm && (
        <div className="add-form-overlay" onClick={(e) => e.target === e.currentTarget && setShowAddForm(false)}>
          <div className="add-form-sheet">
            <div className="form-sheet-header">
              <h2>Add Journey Stop</h2>
              <button className="close-form-btn" onClick={() => setShowAddForm(false)}>✕</button>
            </div>

            <div className="form-body">
              <div className="form-group">
                <label>Outlet Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Westlands Bar & Grill"
                  value={form.outletName}
                  onChange={e => setForm({ ...form, outletName: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Address / Area</label>
                <input
                  type="text"
                  placeholder="e.g. Westlands, Nairobi"
                  value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    placeholder="Name"
                    value={form.contactPerson}
                    onChange={e => setForm({ ...form, contactPerson: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    placeholder="07xx xxx xxx"
                    value={form.contactPhone}
                    onChange={e => setForm({ ...form, contactPhone: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Purpose</label>
                <div className="purpose-grid">
                  {purposeOptions.map(opt => (
                    <button
                      key={opt.value}
                      className={`purpose-chip ${form.purpose === opt.value ? 'purpose-active' : ''}`}
                      style={form.purpose === opt.value ? { borderColor: opt.color, background: `${opt.color}22`, color: opt.color } : {}}
                      onClick={() => setForm({ ...form, purpose: opt.value })}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Visit Order</label>
                  <input
                    type="number"
                    placeholder={`${stops.length + 1}`}
                    min="1"
                    value={form.visitOrder}
                    onChange={e => setForm({ ...form, visitOrder: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  placeholder="Any special instructions..."
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="form-input form-textarea"
                  rows={3}
                />
              </div>
            </div>

            <div className="form-actions">
              <button className="cancel-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
              <button className="submit-btn" onClick={handleAddStop} disabled={actionLoading}>
                {actionLoading ? <><span className="spinner-small"></span> Adding...</> : '+ Add Stop'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop List */}
      {stops.length === 0 ? (
        <div className="empty-journey">
          <div className="empty-journey-icon">🗺️</div>
          <h3>No stops planned today</h3>
          <p>Add your first stop to start planning your route</p>
          <button className="add-first-btn" onClick={() => setShowAddForm(true)}>+ Add First Stop</button>
        </div>
      ) : (
        <div className="stops-list">
          {stops.map((stop, index) => {
            const purposeInfo = getPurposeInfo(stop.purpose);
            const isVisited = stop.status === 'visited';
            const isSkipped = stop.status === 'skipped';
            const isPending = stop.status === 'pending';

            return (
              <div key={stop.id} className={`stop-card ${stop.status}`}>
                {/* Order badge + status line */}
                <div className="stop-order-col">
                  <div className={`order-badge ${stop.status}`}>
                    {isVisited ? '✓' : isSkipped ? '⊘' : stop.visitOrder || index + 1}
                  </div>
                  {index < stops.length - 1 && <div className={`connector-line ${stop.status}`}></div>}
                </div>

                <div className="stop-body">
                  {/* Top row */}
                  <div className="stop-top">
                    <div className="stop-name-block">
                      <h3 className="stop-name">{stop.outletName}</h3>
                      {stop.address && <p className="stop-address">📍 {stop.address}</p>}
                    </div>
                    <div className="stop-badges">
                      <span className="purpose-badge" style={{ color: purposeInfo.color, borderColor: `${purposeInfo.color}44`, background: `${purposeInfo.color}11` }}>
                        {purposeInfo.label}
                      </span>
                      <span className={`status-badge-jp ${stop.status}`}>
                        {isVisited ? '✅ Visited' : isSkipped ? '⏭️ Skipped' : '⏳ Pending'}
                      </span>
                    </div>
                  </div>

                  {/* Contact */}
                  {(stop.contactPerson || stop.contactPhone) && (
                    <div className="stop-contact">
                      {stop.contactPerson && <span>👤 {stop.contactPerson}</span>}
                      {stop.contactPhone && (
                        <a href={`tel:${stop.contactPhone}`} className="contact-phone">📞 {stop.contactPhone}</a>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {stop.notes && <p className="stop-notes">💬 {stop.notes}</p>}

                  {/* Visit info */}
                  {isVisited && stop.visitedAt && (
                    <div className="visit-info">
                      <span className="visit-time">
                        ✅ Visited at {new Date(stop.visitedAt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {stop.visitLocation && (
                        <button
                          className="map-btn-small"
                          onClick={() => openInGoogleMaps(stop.visitLocation.latitude, stop.visitLocation.longitude, stop.outletName)}
                        >
                          🗺️ Map
                        </button>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="stop-actions">
                    {isPending && (
                      <>
                        <button
                          className="action-visited"
                          onClick={() => handleMarkVisited(stop)}
                          disabled={actionLoading}
                        >
                          ✅ Mark Visited
                        </button>
                        <button
                          className="action-skip"
                          onClick={() => handleMarkSkipped(stop)}
                          disabled={actionLoading}
                        >
                          ⏭️ Skip
                        </button>
                        {stop.address && (
                          <button
                            className="action-navigate"
                            onClick={() => openInGoogleMaps(null, null, stop.outletName + ' ' + stop.address)}
                          >
                            🗺️ Navigate
                          </button>
                        )}
                      </>
                    )}
                    {(isVisited || isSkipped) && (
                      <button
                        className="action-undo"
                        onClick={() => handleUndoVisit(stop)}
                      >
                        ↩️ Undo
                      </button>
                    )}
                    <button
                      className="action-delete"
                      onClick={() => handleDeleteStop(stop)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

export default JourneyPlan;
